import { Observable } from "@apollo/client";
import { onError, ErrorResponse } from "@apollo/client/link/error";
import type { AuthClient } from "./types";

/**
 * Detects an expired/invalid access token. The backend returns auth
 * failures in two shapes:
 *
 *  1. HTTP 401 — surfaces as `networkError.statusCode === 401`
 *  2. HTTP 200 with `graphQLErrors: [{ message: "not authenticated" }]` —
 *     Django's GraphQL layer returns 200 with the error in the body when
 *     the JWT is expired or missing.
 */
const NOT_AUTHENTICATED_MESSAGE = "not authenticated";

const isAuthFailure = (
  networkError: unknown,
  graphQLErrors: ReadonlyArray<{ message: string }> | undefined,
): boolean => {
  if (
    networkError != null &&
    typeof networkError === "object" &&
    "statusCode" in networkError &&
    (networkError as { statusCode?: number }).statusCode === 401
  ) {
    return true;
  }
  if (graphQLErrors?.some((err) => err.message.toLowerCase() === NOT_AUTHENTICATED_MESSAGE)) {
    return true;
  }
  return false;
};

const AUTH_RETRY_KEY = "__authRetried";

interface AuthErrorLinkConfig {
  authClient: AuthClient;
  onGraphQLErrors?: (errors: ReadonlyArray<{ message: string }>) => void;
  onNetworkError?: (error: Error) => void;
}

/**
 * Creates an Apollo error link that detects 401 auth failures, refreshes
 * the token via `authClient.refresh()`, and retries the operation once
 * with the new token. Non-auth errors are forwarded to optional callbacks.
 *
 * A per-operation retry guard prevents infinite loops if the backend keeps
 * rejecting freshly-issued tokens.
 */
export const createAuthErrorLink = ({ authClient, onGraphQLErrors, onNetworkError }: AuthErrorLinkConfig) =>
  onError(({ graphQLErrors, networkError, operation, forward }: ErrorResponse) => {
    const alreadyRetried = operation.getContext()[AUTH_RETRY_KEY];
    if (!alreadyRetried && isAuthFailure(networkError, graphQLErrors)) {
      operation.setContext({ [AUTH_RETRY_KEY]: true });
      return new Observable((observer) => {
        authClient
          .refresh()
          .then((newToken) => {
            if (!newToken) {
              observer.error(networkError ?? new Error("Authentication failed"));
              return;
            }
            operation.setContext(({ headers = {} }: { headers?: Record<string, string> }) => ({
              headers: {
                ...headers,
                Authorization: `Bearer ${newToken}`,
              },
            }));
            forward(operation).subscribe({
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            });
          })
          .catch((err) => observer.error(err));
      });
    }

    if (graphQLErrors) {
      onGraphQLErrors?.(graphQLErrors);
    }

    if (networkError) {
      onNetworkError?.(networkError);
    }
  });
